import { FC, useEffect, useMemo, useState } from 'react';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';
import { IListFilesRequest } from 'Data/interfaces/ListagemArquivos/IListFilesRequest';
import { IFileData } from 'Data/interfaces/ListagemArquivos/IListFilesResponse';
import { IDisciplineViewerList } from 'Data/interfaces/FederatedViewer/IDisciplineViewerList';
import { IMoveFileRequest } from 'Data/interfaces/ListagemArquivos/IMoveFileRequest';
import { IGlobalReducerState } from 'Store/Base/interface/IGlobalReducerState';
import { NomenclaturaActions } from 'Store/Nomenclatura/Nomenclatura.actions';
import { useQueryMoveFileList } from 'Hooks/files/useQueryMoveFileList';
import { useQueryMoveFileMove } from 'Hooks/files/useQueryMoveFileMove';
import { useQueryUniqueObra } from 'Hooks/obras/useQueryUniqueObra';
import { useFeatureFlag } from 'Hooks/useFeatureFlag';
import { getDisciplineDisplayIcon } from 'Utils/ObraUtils';
import LoadingUploadDisciplineList from 'Components/UI/CustomLoading/LoadingUploadDisciplineList';
import Button from 'Components/UI/Button';
import Modal from 'Components/UI/Modal';
import { Icon, IconName } from 'Components/UI';
import MoveFilesModalHeader from './components/MoveFilesModalHeader';
import MoveFilesModalListDisciplines from './components/MoveFilesModalListDisciplines';
import MoveFilesModalListChildren, { DEFAULT_OBSOLETE_FOLDER_ID } from './components/MoveFilesModalListChildren';
import AlertMoveFiles from './components/AlertMoveFiles';
import {
  TAlertMoveFilesType,
  getAlertMoveFilesTitle,
  getAlertMoveFilesDescriptionHeader,
  getAlertMoveFilesDescriptionFooter,
  IAlertMoveFilesData,
} from './alertUtils';
import { Buttons, Main, Wrapper } from './styles';
import ObrasApi from 'Data/Obras.api';

export interface ICurrentBreadCrumbItem {
  folderId: string;
  name: string;
}

export interface IMoveFilesModal {
  visible: boolean;
  files: IFileData[];
  constructionSiteId: number;
  oldFolderId: string;
  externalLoading?: boolean;
  currentDisciplineId?: number;
  currentBreadCrumb?: ICurrentBreadCrumbItem[];
  onCancel: () => void;
  onNewFolder: (file?: IFileData, folderDestination?: string) => void;
}

export interface IFileFinalPath {
  folderId: string;
  disciplineApiFolderId: string;
  ConstructionSiteDisciplinesId: number;
  trackFolderName?: string;
  isObsoleteFolder?: boolean;
}

export interface IBreadcrumbPath {
  name: string;
  folderId: string;
  ConstructionSiteDisciplinesId?: number;
  isObsoleteFolder?: boolean;
}

interface IFolderDataId {
  folderId: string;
  isObsolete?: boolean;
  parentId?: string;
  constructionSiteDisciplinesId: number;
}

const MoveFilesModal: FC<Props> = ({
  visible,
  files,
  constructionSiteId,
  oldFolderId,
  currentDisciplineId,
  currentBreadCrumb,
  nomenclaturaData,
  onCancel,
  onNewFolder,
}) => {
  const newMoveFilesFeatureFlag = useFeatureFlag('new-move-files-feature');
  const newMoveFilesFeature =
    newMoveFilesFeatureFlag.enabled && newMoveFilesFeatureFlag.value === 'test';

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [fileFinalPath, setFileFinalPath] = useState({} as IFileFinalPath);
  const [disciplineFolder, setDisciplineFolder] = useState<IDisciplineViewerList | null>(null);
  const [breadCrumbs, setBreadCrumbs] = useState<IBreadcrumbPath[]>([]);
  const [selectedFolderData, setSelectedFolderData] = useState<IFolderDataId | null>(null);
  const [searchDisciplineFile, setSearchDisciplineFile] = useState('');
  const [searchFile, setSearchFile] = useState('');
  const [sentFile, setSentFile] = useState(false);
  const [alertMoveFilesVisible, setAlertMoveFilesVisible] = useState(true);
  const [alertMoveFilesType, setAlertMoveFilesType] = useState<IAlertMoveFilesData[]>([]);
  const [pendingMoveRequest, setPendingMoveRequest] = useState<IMoveFileRequest | null>(null);
  const [initializedCurrentFolder, setInitializedCurrentFolder] = useState(false);

  const request = useMemo(() => {
    const requestData: IListFilesRequest = {
      constructionSiteId,
      folderId: selectedFolderData?.folderId || '',
      loadObsoleteFiles: false,
      ignoreBreadCrumb: true,
    }
    return requestData
  }, [selectedFolderData, constructionSiteId]);
  const {
    data: listFilesData,
    isLoading: listFilesLoading,
  } = useQueryMoveFileList(request);
  const { moveFile, isLoading: moveFileLoading } = useQueryMoveFileMove();
  const { data: disciplineObra, isFetching: disciplinesLoading } = useQueryUniqueObra({
    csId: constructionSiteId,
    navigate,
  });

  const filteredDisciplines = useMemo(() => {
    if (disciplineObra?.Disciplines && searchDisciplineFile) {
      return disciplineObra?.Disciplines?.filter((discipline: any) => {
        if (discipline.CustomName) {
          return discipline?.CustomName.toLowerCase().includes(
            searchDisciplineFile.toLowerCase(),
          );
        } else {
          return discipline?.Discipline.Name.toLowerCase().includes(
            searchDisciplineFile.toLowerCase(),
          );
        }
      });
    }
    return disciplineObra?.Disciplines;
  }, [disciplineObra?.Disciplines, searchDisciplineFile]);

  const filteredFiles = useMemo(() => {
    if (listFilesData.ListFiles.length > 0 && searchFile) {
      return listFilesData.ListFiles.filter((file) => {
        return file.Name.toLowerCase().includes(searchFile.toLowerCase());
      });
    }
    return listFilesData.ListFiles;
  }, [listFilesData, searchFile]);

  const handleDisciplineFolder = (currDisciplineFolder: IDisciplineViewerList | null) => {
    setDisciplineFolder(currDisciplineFolder);
    clearAllSearch();
  };

  const handleIconLeft = (discipline: any): IconName => getDisciplineDisplayIcon(discipline);

  const handleOpenFolder = (
    folderId: string,
    ConstructionSiteDisciplinesId?: number,
    folderName?: string,
    isObsoleteFolder?: boolean,
  ) => {
    clearAllSearch();
    setSelectedFolderData(prev => {
      if (isObsoleteFolder) {
        return {
          folderId: listFilesData?.ObsoleteFolderId || folderId,
          parentId: prev?.parentId,
          constructionSiteDisciplinesId: ConstructionSiteDisciplinesId || disciplineFolder?.ConstructionSiteDisciplinesId || 0,
          isObsolete: isObsoleteFolder,
        }
      }
      return {
        folderId,
        parentId: folderId,
        constructionSiteDisciplinesId: ConstructionSiteDisciplinesId || disciplineFolder?.ConstructionSiteDisciplinesId || 0,
        isObsolete: isObsoleteFolder,
      }
    });

    const filePath: IFileFinalPath = {
      folderId,
      disciplineApiFolderId: disciplineFolder?.DisciplineApiFolderId || folderId,
      ConstructionSiteDisciplinesId:
        disciplineFolder?.ConstructionSiteDisciplinesId ||
        ConstructionSiteDisciplinesId ||
        0,
      trackFolderName: folderName,
      isObsoleteFolder,
    }
    setFileFinalPath(filePath);

    setBreadCrumbs((prevState) => {
      const folderIndex = prevState.findIndex((state) => state.folderId === folderId);
      if (folderIndex === -1 && folderName) {
        return [...prevState, {
          folderId: isObsoleteFolder ? (listFilesData?.ObsoleteFolderId || folderId) : folderId,
          name: folderName,
          ConstructionSiteDisciplinesId: disciplineFolder?.ConstructionSiteDisciplinesId || ConstructionSiteDisciplinesId,
          isObsoleteFolder,
        }];
      }
      if (folderIndex === -1 && !folderName) {
        return [];
      }
      if (folderIndex >= 0 && folderName) {
        if (folderIndex === 0) {
          return [{
            folderId: isObsoleteFolder ? (listFilesData?.ObsoleteFolderId || folderId) : folderId,
            name: folderName,
            ConstructionSiteDisciplinesId: disciplineFolder?.ConstructionSiteDisciplinesId || ConstructionSiteDisciplinesId,
            isObsoleteFolder,
          }];
        } else {
          return prevState.slice(0, folderIndex + 1);
        }
      }
      return prevState;
    });
  };

  const handleListDisciplines = (ifFirstRender?: boolean) => {
    setBreadCrumbs([]);
    setDisciplineFolder(null);
    clearAllSearch();
  };

  const closeModal = () => {
    setFileFinalPath({} as any);
    setDisciplineFolder(null);
    clearAllSearch();
    onCancel();
  };

  const isLoading = disciplinesLoading || listFilesLoading || moveFileLoading;

  const confirmUploadFilePath = async () => {
    if (disciplineFolder && files.length > 0 && fileFinalPath) {
      try {
        const fileIds = files.map(file => file.Identifier.ApiId);

        const request: IMoveFileRequest = {
          ConstructionSiteId: constructionSiteId || 0,
          CurrentFolderId: oldFolderId,
          FilesId: fileIds,
          NewFolderId: fileFinalPath.isObsoleteFolder
            ? (fileFinalPath.folderId === DEFAULT_OBSOLETE_FOLDER_ID ? selectedFolderData?.parentId || '' : fileFinalPath.folderId)
            : fileFinalPath.folderId,
          DisciplineApiFolderId: fileFinalPath.disciplineApiFolderId,
          FileRenames: {},
          IsMovingToObsoleteFolder: fileFinalPath.isObsoleteFolder || false,
        };

        const { data } = await ObrasApi.checkMoveFile(request);

        const conflictPriority: {
          flag: 'IsMovingWithSameVersion' | 'IsMovingWithLowerVersion' | 'IsMovingWithHigherVersion' | 'IsMovingLatestToObsolete';
          type: IAlertMoveFilesData['type'];
        }[] = [
            { flag: 'IsMovingWithSameVersion', type: 'version-equal' },
            { flag: 'IsMovingWithLowerVersion', type: 'version-down' },
            { flag: 'IsMovingWithHigherVersion', type: 'version-up' },
            { flag: 'IsMovingLatestToObsolete', type: 'version-obsolete' },
          ];

        const allConflicts: IAlertMoveFilesData[] = [];
        for (const file of data.data) {
          for (const { flag, type } of conflictPriority) {
            if (file[flag]) {
              allConflicts.push({
                type,
                filesData: {
                  FileId: file.Identifier.ApiId,
                  FileName: file.Name,
                  Revision: file.Revision,
                },
                checkData: file.ExistingFileInDestination ? {
                  ...file.ExistingFileInDestination,
                  fileNameNewRevision: file.FileNameNewRevision,
                  fileNameCopy: file.FileNameCopy,
                } : undefined,
              });
              if (!!file.FileNameNewRevision) {
                request.FileRenames[file.Identifier.ApiId] = file.FileNameNewRevision;
              }
              break;
            }
          }
        }

        if (allConflicts.length > 0) {
          setPendingMoveRequest(request);
          openAlertMoveFiles(allConflicts);
          return;
        }

        setSentFile(true);
        moveFile(request);
      } catch (error) {
        console.error('Erro ao mover arquivos:', error);
      }
    }
  };

  const handleSearch = (value: string) => {
    if (!disciplineFolder) {
      setSearchFile('');
      setSearchDisciplineFile(value);
    } else {
      setSearchFile(value);
      setSearchDisciplineFile('');
    }
  };

  const clearAllSearch = () => {
    setSearchDisciplineFile('');
    setSearchFile('');
  };

  const handleNewFolder = () => {
    if (!fileFinalPath.folderId) { return; }
    onNewFolder(undefined, fileFinalPath.folderId);
  };

  const handleChangeFileName = (fileId: string, value: string) => {
    setPendingMoveRequest(prev => {
      if (!prev) return null;
      const newState = prev;
      newState.FileRenames[fileId] = value;
      return { ...newState };
    });
  }

  const openAlertMoveFiles = (data: IAlertMoveFilesData[]) => {
    setAlertMoveFilesType(data);
    setAlertMoveFilesVisible(true);
  };

  const closeAlertMoveFiles = () => {
    setAlertMoveFilesType([]);
    setAlertMoveFilesVisible(false);
  };

  const confirmAlertMoveFiles = () => {
    if (!pendingMoveRequest) return;
    setSentFile(true);
    moveFile(pendingMoveRequest);
    setPendingMoveRequest(null);
    closeAlertMoveFiles();
  };

  useEffect(() => {
    if (!currentDisciplineId) {
      handleListDisciplines(true);
    }

    if (!nomenclaturaData.VersionFields) {
      dispatch(NomenclaturaActions.getNomenclature());
    }
  }, []);

  useEffect(() => {
    if (
      initializedCurrentFolder ||
      !currentDisciplineId ||
      !currentBreadCrumb ||
      currentBreadCrumb.length === 0 ||
      !disciplineObra?.Disciplines
    ) {
      return;
    }

    const discipline = disciplineObra.Disciplines.find(
      (item: IDisciplineViewerList) => item.ConstructionSiteDisciplinesId === currentDisciplineId,
    );

    if (discipline) {
      setDisciplineFolder(discipline);

      const lastFolder = currentBreadCrumb[currentBreadCrumb.length - 1];
      setBreadCrumbs(
        currentBreadCrumb.slice(1).map((folder) => ({
          folderId: folder.folderId,
          name: folder.name,
          ConstructionSiteDisciplinesId: currentDisciplineId,
        })),
      );
      setSelectedFolderData({
        folderId: lastFolder.folderId,
        parentId: lastFolder.folderId,
        constructionSiteDisciplinesId: currentDisciplineId,
      });
      setFileFinalPath({
        folderId: lastFolder.folderId,
        disciplineApiFolderId: discipline.DisciplineApiFolderId,
        ConstructionSiteDisciplinesId: currentDisciplineId,
        trackFolderName: lastFolder.name,
      });
    }
    setInitializedCurrentFolder(true);
  }, [currentDisciplineId, currentBreadCrumb, disciplineObra, initializedCurrentFolder]);

  useEffect(() => {
    if (sentFile && !moveFileLoading) {
      closeModal();
    }
  }, [sentFile, moveFileLoading]);

  const buttons = (
    <Buttons>
      <Button
        type='text'
        className='buttonNewFolder'
        onClick={handleNewFolder}
      >
        <Icon icon='adicionarPasta' customSize={16} className='iconNewFolder' />
        Nova Pasta
      </Button>
      <div className='rightButtons'>
        <Button
          type='text'
          className='buttonCancel'
          onClick={closeModal}
        >
          Cancelar
        </Button>
        <Button
          type='primary'
          className='buttonConfirm'
          disabled={!disciplineFolder}
          loading={isLoading}
          onClick={confirmUploadFilePath}
        >
          Mover
        </Button>
      </div>
    </Buttons>
  );

  return (
    <>
      <Modal
        title={files.length > 1
          ? `Mover ${files.length} arquivos`
          : `Mover "${files[0].Name}"`
        }
        width={400}
        visible={visible}
        footerButtons={buttons}
        footerActive={false}
        customClassName
        newHeader
        onCancel={closeModal}
      >
        <Wrapper>
          <MoveFilesModalHeader
            value={searchDisciplineFile || searchFile}
            disciplineFolder={disciplineFolder}
            isLoading={isLoading}
            breadCrumbs={breadCrumbs}
            onSearch={handleSearch}
            onListDisciplines={handleListDisciplines}
            onOpenFolder={handleOpenFolder}
            onIconLeft={handleIconLeft}
          />
          <Main>
            {(isLoading || (!filteredDisciplines && filteredFiles.length <= 0)) && (
              <LoadingUploadDisciplineList multiple={12} />
            )}
            {!isLoading && !disciplineFolder && (
              <MoveFilesModalListDisciplines
                filteredDisciplines={filteredDisciplines}
                onDisciplineFolder={handleDisciplineFolder}
                onOpenFolder={handleOpenFolder}
                onIconLeft={handleIconLeft}
                searchDisciplineFile={searchDisciplineFile}
              />
            )}
            {!isLoading && !!disciplineFolder && (
              <MoveFilesModalListChildren
                files={files}
                filteredFiles={filteredFiles}
                disciplineFolder={disciplineFolder}
                searchFile={searchFile}
                obsoleteFolderId={selectedFolderData?.isObsolete ? selectedFolderData.folderId : undefined}
                isObsoleteFolder={fileFinalPath.isObsoleteFolder}
                onOpenFolder={handleOpenFolder}
              />
            )}
          </Main>
        </Wrapper>
      </Modal>

      {alertMoveFilesVisible && alertMoveFilesType.length > 0 && (
        <AlertMoveFiles
          visible={alertMoveFilesVisible}
          items={alertMoveFilesType}
          onChangeFileName={handleChangeFileName}
          onCancel={closeAlertMoveFiles}
          onAction={confirmAlertMoveFiles}
        />
      )}
    </>
  );
};

const mapState = (state: IGlobalReducerState) => ({
  loading: state.federatedViewer.isLoading,
  nomenclaturaData: state.nomenclatura.NomenclatureData,
});

const connector = connect(mapState);

type PropsFromRedux = ConnectedProps<typeof connector>;
type Props = PropsFromRedux & IMoveFilesModal;

export default connector(MoveFilesModal);
